import { Link } from "react-router-dom";
import Section from "../components/Section";
import Container from "../components/Container";
import { FaDna, FaVial, FaUsers } from "react-icons/fa";
import CountUp from "react-countup";

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="bg-gradient-to-b from-rose-50 to-white">
        <Container className="py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight text-slate-900">
              Know Thalassemia
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              Inherited blood disorder affecting haemoglobin. Early testing and
              awareness can change lives.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/testing-locator"
                className="px-5 py-3 rounded-lg bg-rose-600 text-white font-medium"
              >
                Find Testing Centers
              </Link>
              <Link
                to="/about"
                className="px-5 py-3 rounded-lg border border-rose-600 text-rose-700 font-medium"
              >
                Learn More
              </Link>
            </div>

            {/* HERO HIGHLIGHTS */}
            <div className="mt-8 grid grid-cols-3 gap-4 text-center">
              <div>
                <FaDna className="mx-auto text-rose-600 text-xl" />
                <p className="mt-2 text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">Genetic</span>
                  <br />
                  not contagious
                </p>
              </div>
              <div>
                <FaVial className="mx-auto text-rose-600 text-xl" />
                <p className="mt-2 text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">Testing</span>
                  <br />
                  before marriage
                </p>
              </div>
              <div>
                <FaUsers className="mx-auto text-rose-600 text-xl" />
                <p className="mt-2 text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">Support</span>
                  <br />
                  patients & families
                </p>
              </div>
            </div>
          </div>

          {/* Right: actual hero image */}
          <div className="relative">
            <picture>
              <source srcSet="/hero.jpeg" type="image/webp" />
              <img
                src="/hero.jpg"
                alt="Students promoting thalassemia awareness"
                className="w-full aspect-video object-cover rounded-2xl border shadow-sm"
                loading="eager"
                fetchpriority="high"
              />
            </picture>
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-t from-black/10 to-transparent" />
          </div>
        </Container>
      </section>

      {/* 3 CARDS */}
      <Section eyebrow="Overview" title="Awareness, Prevention & Care">
        <div className="grid md:grid-cols-3 gap-6">
          <Card
            title="What is Thalassemia?"
            body="RBCs carry oxygen. Thalassemia affects haemoglobin leading to anaemia."
            to="/about"
          />
          <Card
            title="Why Testing Matters"
            body="If both partners are carriers, there is a 25% chance of a child with thalassemia major."
            to="/quiz"
            cta="Take Myths vs Facts Quiz"
          />
          <Card
            title="Get Support"
            body="Find NGOs, hospitals, and helplines for guidance and care."
            to="/support"
          />
        </div>
      </Section>

      {/* INDIA DATA COUNTERS */}
      <section className="bg-rose-600 text-white py-16 mb-16">
        <Container className="grid md:grid-cols-4 gap-10 text-center">
          <div>
            <div className="text-3xl font-bold">
              <CountUp end={100000} duration={3} separator="," />+
            </div>
            <p className="mt-2 text-sm">Patients in India</p>
          </div>
          <div>
            <div className="text-3xl font-bold">
              1 in <CountUp end={25} duration={3} /> 
            </div>
            <p className="mt-2 text-sm">Indians are carriers</p>
          </div>
          <div>
            <div className="text-3xl font-bold">
              <CountUp end={10000} duration={3} separator="," />+
            </div>
            <p className="mt-2 text-sm">New cases each year</p>
          </div>
          <div>
            <div className="text-3xl font-bold">
              <CountUp end={80} duration={3} />%
            </div>
            <p className="mt-2 text-sm">Severe cases preventable</p>
          </div>
        </Container>
      </section>

      {/* SHARE */}
      <Section pad="pb-24">
        <div className="rounded-2xl border bg-slate-50 p-8 text-center">
          <h3 className="text-xl font-semibold text-slate-900">
            Spread Awareness
          </h3>
          <p className="text-slate-600 mt-1">
            Share this website with your friends and family.
          </p>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(
              "Know Thalassemia — Awareness, Prevention & Care: https://example.com"
            )}`}
            target="_blank"
            rel="noreferrer"
            className="inline-block mt-4 px-5 py-3 rounded-lg bg-emerald-600 text-white font-medium"
          >
            Share on WhatsApp
          </a>
        </div>
      </Section>
    </>
  );
}

function Card({ title, body, to, cta }) {
  return (
    <div className="rounded-xl border p-6 bg-white shadow-sm">
      <h3 className="font-semibold text-lg text-slate-900">{title}</h3>
      <p className="text-slate-600 mt-2">{body}</p>
      <Link
        to={to}
        className="inline-block mt-3 text-rose-700 font-medium hover:underline"
      >
        {cta || "Learn more →"}
      </Link>
    </div>
  );
}
